/**
 * BrandForm.jsx
 * Create/edit form for a brand profile.
 * Colours are entered as a comma-separated list of hex codes and sent as an array.
 */

import { useState } from 'react';

const EMPTY = {
  name: '',
  product_summary: '',
  tone: '',
  audience: '',
  banned_claims: '',
  default_cta: '',
  default_hashtags: '',
  target_url: '',
  image_style_prompt: '',
  default_aspect: '4:5',
};

const ASPECTS = [
  { key: '4:5', label: '4:5 portrait (1080×1350) — best for Instagram feed' },
  { key: '1:1', label: '1:1 square (1080×1080) — safe on both platforms' },
  { key: '1.91:1', label: '1.91:1 landscape (1200×628) — Facebook link posts' },
];

export default function BrandForm({ brand, busy, onSave, onCancel }) {
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    ...(brand ?? {}),
    colorsText: (brand?.colors ?? []).join(', '),
  }));

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  function submit(e) {
    e.preventDefault();
    const colors = form.colorsText
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);

    const { colorsText, ...rest } = form;
    const payload = Object.fromEntries(
      Object.keys(EMPTY).map((k) => [k, rest[k] ?? EMPTY[k]]),
    );
    onSave({ ...payload, colors });
  }

  return (
    <form className="card" onSubmit={submit}>
      <div className="field">
        <label htmlFor="name">Brand name</label>
        <input id="name" type="text" value={form.name} onChange={set('name')} required />
      </div>

      <div className="field">
        <label htmlFor="product_summary">What it does, in your own words</label>
        <textarea
          id="product_summary"
          value={form.product_summary}
          onChange={set('product_summary')}
          placeholder="PeekoType turns speech into accurate text on any device, so you can capture notes, emails and ideas without typing."
        />
        <p className="hint">Written as you'd say it to a customer, not as marketing copy.</p>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="tone">Tone of voice</label>
          <textarea
            id="tone"
            value={form.tone}
            onChange={set('tone')}
            placeholder="Modern, clear, useful. Productivity-focused. No hype, no exclamation marks."
          />
        </div>
        <div className="field">
          <label htmlFor="audience">Audience</label>
          <textarea
            id="audience"
            value={form.audience}
            onChange={set('audience')}
            placeholder="Professionals, students and content creators who write a lot and type slowly."
          />
        </div>
      </div>

      <div className="field">
        <label htmlFor="banned_claims">Never claim</label>
        <textarea
          id="banned_claims"
          value={form.banned_claims}
          onChange={set('banned_claims')}
          placeholder="No accuracy percentages. No pricing promises. Never name competitors."
        />
        <p className="hint">
          Injected as a hard rule into every generation. The most useful field on this form.
        </p>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="default_cta">Default call to action</label>
          <input
            id="default_cta"
            type="text"
            value={form.default_cta}
            onChange={set('default_cta')}
            placeholder="Download free on Google Play"
          />
        </div>
        <div className="field">
          <label htmlFor="target_url">Target URL</label>
          <input
            id="target_url"
            type="url"
            value={form.target_url}
            onChange={set('target_url')}
            placeholder="https://peekotype.com"
          />
        </div>
      </div>

      <div className="field">
        <label htmlFor="default_hashtags">Default hashtags</label>
        <input
          id="default_hashtags"
          type="text"
          value={form.default_hashtags}
          onChange={set('default_hashtags')}
          placeholder="#productivity #dictation #speechtotext"
        />
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="colorsText">Brand colours</label>
          <input
            id="colorsText"
            type="text"
            value={form.colorsText}
            onChange={set('colorsText')}
            placeholder="#4f8cff, #0e1116"
          />
          <p className="hint">Hex codes, comma separated.</p>
        </div>
        <div className="field">
          <label htmlFor="default_aspect">Default image ratio</label>
          <select id="default_aspect" value={form.default_aspect} onChange={set('default_aspect')}>
            {ASPECTS.map((a) => (
              <option key={a.key} value={a.key}>
                {a.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="field">
        <label htmlFor="image_style_prompt">Image style</label>
        <textarea
          id="image_style_prompt"
          value={form.image_style_prompt}
          onChange={set('image_style_prompt')}
          placeholder="Clean, bold, mobile-first. Lots of whitespace. Deep navy background with one accent colour. No stock-photo people."
        />
        <p className="hint">Prepended to every image generation for this brand (Phase 4).</p>
      </div>

      <div className="btn-row">
        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy ? 'Saving…' : 'Save brand'}
        </button>
        <button type="button" className="btn" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
